import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Typography, Container, List, ListItem, ListItemButton, ListItemText,
  IconButton, Button, Divider, useMediaQuery, useTheme, Drawer
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MenuIcon from '@mui/icons-material/Menu';
import ChatIcon from '@mui/icons-material/Chat';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ChatInterface from '../components/ChatInterface';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';

interface Conversation {
  id: number;
  title: string;
  created_at: string;
  updated_at: string;
}

const ChatPage: React.FC = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(
    urlConversationId ? parseInt(urlConversationId, 10) : null
  );
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  // Stable key that only changes when user clicks "New Chat" or selects a different conversation
  const [chatSessionKey, setChatSessionKey] = useState(0);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [isAuthenticated, authLoading, navigate]);

  const loadConversations = useCallback(async () => {
    try {
      const data = await api.get('/api/chat/conversations');
      if (data.success) {
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const handleNewConversation = () => {
    setActiveConversationId(null);
    setChatSessionKey(prev => prev + 1); // Force remount
    if (isMobile) setSidebarOpen(false);
  };

  const handleSelectConversation = (id: number) => {
    setActiveConversationId(id);
    setChatSessionKey(prev => prev + 1); // Force remount to load selected conversation
    if (isMobile) setSidebarOpen(false);
  };

  const handleConversationCreated = (id: number) => {
    setActiveConversationId(id);
    loadConversations(); // Refresh sidebar list — do NOT change chatSessionKey
  };

  const handleDeleteConversation = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const data = await api.delete(`/api/chat/conversations/${id}`);
      if (data.success) {
        setConversations(prev => prev.filter(c => c.id !== id));
        if (activeConversationId === id) {
          setActiveConversationId(null);
        }
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  if (authLoading) return null;

  const sidebarContent = (
    <Box sx={{ width: isMobile ? 280 : 260, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewConversation}
        >
          New Chat
        </Button>
      </Box>
      <Divider />
      <List sx={{ flex: 1, overflow: 'auto', py: 0 }}>
        {conversations.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No conversations yet
            </Typography>
          </Box>
        ) : (
          conversations.map(conv => (
            <ListItem
              key={conv.id}
              disablePadding
              secondaryAction={
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => handleDeleteConversation(conv.id, e)}
                  sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              }
            >
              <ListItemButton
                selected={activeConversationId === conv.id}
                onClick={() => handleSelectConversation(conv.id)}
                sx={{ pr: 5 }}
              >
                <ChatIcon sx={{ mr: 1.5, fontSize: 18, color: 'text.secondary' }} />
                <ListItemText
                  primary={conv.title || 'Untitled'}
                  primaryTypographyProps={{
                    noWrap: true,
                    fontSize: '0.875rem',
                  }}
                  secondary={new Date(conv.updated_at).toLocaleDateString()}
                  secondaryTypographyProps={{ fontSize: '0.75rem' }}
                />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Sidebar - desktop */}
        {!isMobile && (
          <Box sx={{
            width: 260,
            borderRight: '1px solid rgba(0,0,0,0.1)',
            backgroundColor: '#fafafa',
            flexShrink: 0,
          }}>
            {sidebarContent}
          </Box>
        )}

        {/* Sidebar - mobile drawer */}
        {isMobile && (
          <Drawer
            open={sidebarOpen}
            onClose={() => setSidebarOpen(false)}
            sx={{ '& .MuiDrawer-paper': { width: 280 } }}
          >
            {sidebarContent}
          </Drawer>
        )}

        {/* Main chat area */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Mobile toolbar */}
          {isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1, borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
              <IconButton onClick={() => setSidebarOpen(true)}>
                <MenuIcon />
              </IconButton>
              <Typography variant="subtitle2" color="text.secondary" sx={{ ml: 1 }}>
                {activeConversationId
                  ? conversations.find(c => c.id === activeConversationId)?.title || 'Chat'
                  : 'New Chat'}
              </Typography>
            </Box>
          )}

          {/* Chat interface */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <ChatInterface
              key={chatSessionKey}
              conversationId={activeConversationId || undefined}
              onConversationCreated={handleConversationCreated}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatPage;
